import React from 'react';

// ─── Base building blocks ───
const Bone = ({ className = '' }) => (
  <div className={`rounded-lg bg-slate-200 dark:bg-slate-700 ${className}`} />
);

const CardShell = ({ children, className = '' }) => (
  <div className={`rounded-2xl border p-5 border-slate-100 dark:border-slate-700/60 bg-white dark:bg-slate-800 ${className}`}>
    {children}
  </div>
);

// ─── Dashboard Skeleton ───
export function DashboardSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      {/* Title */}
      <div className="space-y-2">
        <Bone className="h-7 w-48" />
        <Bone className="h-4 w-64" />
      </div>

      {/* Hero metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <CardShell key={i}>
            <Bone className="h-3 w-20 mb-3" />
            <Bone className="h-8 w-28 mb-2" />
            <Bone className="h-3 w-16" />
          </CardShell>
        ))}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CardShell className="h-64 flex items-center justify-center">
          <div className="w-40 h-40 rounded-full border-[20px] border-slate-200 dark:border-slate-700" />
        </CardShell>
        <CardShell className="h-64">
          <Bone className="h-4 w-32 mb-4" />
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-3">
                <Bone className="h-3 w-3 rounded-full" />
                <Bone className="h-3 flex-1" />
                <Bone className="h-3 w-16" />
              </div>
            ))}
          </div>
        </CardShell>
      </div>

      {/* Holdings table */}
      <CardShell>
        <Bone className="h-5 w-36 mb-4" />
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center gap-4">
              <Bone className="w-10 h-10 rounded-full flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <Bone className="h-4 w-20" />
                <Bone className="h-3 w-32" />
              </div>
              <div className="text-right space-y-2">
                <Bone className="h-4 w-24 ml-auto" />
                <Bone className="h-3 w-16 ml-auto" />
              </div>
            </div>
          ))}
        </div>
      </CardShell>
    </div>
  );
}

// ─── Transactions Skeleton ───
export function TransactionsSkeleton() {
  return (
    <div className="space-y-5 animate-pulse">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Bone className="h-7 w-44" />
          <Bone className="h-4 w-56" />
        </div>
        <Bone className="h-10 w-32 rounded-xl" />
      </div>

      {/* Sub-tab bar */}
      <Bone className="h-12 w-full rounded-2xl" />

      {/* Table header */}
      <div className="flex items-center gap-4 px-4 py-3">
        {[60, 80, 60, 80, 80, 60].map((w, i) => (
          <Bone key={i} className="h-3" style={{ width: w }} />
        ))}
      </div>

      {/* Table rows */}
      <CardShell className="divide-y divide-slate-100 dark:divide-slate-700/40 p-0">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="flex items-center gap-4 px-5 py-4">
            <Bone className="w-8 h-8 rounded-lg flex-shrink-0" />
            <Bone className="h-4 w-16" />
            <Bone className="h-4 w-12" />
            <Bone className="h-4 w-20 flex-1" />
            <Bone className="h-4 w-20" />
            <Bone className="h-4 w-16" />
          </div>
        ))}
      </CardShell>
    </div>
  );
}

// ─── Watchlist Skeleton ───
export function WatchlistSkeleton() {
  return (
    <div className="space-y-5 animate-pulse">
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Bone className="h-7 w-36" />
          <Bone className="h-4 w-56" />
        </div>
        <Bone className="h-10 w-28 rounded-xl" />
      </div>

      {/* List tabs */}
      <div className="flex gap-2">
        {[...Array(3)].map((_, i) => (
          <Bone key={i} className="h-9 w-24 rounded-xl" />
        ))}
      </div>

      {/* Stock rows */}
      <div className="space-y-2">
        {[...Array(6)].map((_, i) => (
          <CardShell key={i} className="flex items-center gap-4 p-4">
            <Bone className="w-10 h-10 rounded-full flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <Bone className="h-4 w-16" />
              <Bone className="h-3 w-28" />
            </div>
            <div className="text-right space-y-2">
              <Bone className="h-5 w-20 ml-auto" />
              <Bone className="h-3 w-14 ml-auto" />
            </div>
          </CardShell>
        ))}
      </div>
    </div>
  );
}

// ─── Analytics Skeleton ───
export function AnalyticsSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Bone className="h-7 w-44" />
          <Bone className="h-4 w-64" />
        </div>
        <div className="flex gap-2">
          <Bone className="h-10 w-28 rounded-xl" />
          <Bone className="h-10 w-36 rounded-xl" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <CardShell className="lg:col-span-2 h-72">
          <Bone className="h-5 w-40 mb-4" />
          <Bone className="h-48 w-full rounded-xl" />
        </CardShell>
        <CardShell className="h-72">
          <Bone className="h-5 w-32 mb-4" />
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="space-y-1">
                <Bone className="h-3 w-24" />
                <Bone className="h-6 w-32" />
              </div>
            ))}
          </div>
        </CardShell>
      </div>
    </div>
  );
}

// ─── Markets Skeleton ───
export function MarketsSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Bone className="h-7 w-40" />
          <Bone className="h-4 w-64" />
        </div>
        <Bone className="h-10 w-48 rounded-xl" />
      </div>

      {/* Market indices */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[...Array(4)].map((_, i) => (
          <CardShell key={i}>
            <Bone className="h-3 w-16 mb-2" />
            <Bone className="h-7 w-24 mb-1" />
            <Bone className="h-3 w-20" />
          </CardShell>
        ))}
      </div>

      {/* News / Content area */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {[...Array(4)].map((_, i) => (
          <CardShell key={i} className="flex gap-3 p-4">
            <Bone className="w-16 h-16 rounded-xl flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <Bone className="h-4 w-full" />
              <Bone className="h-3 w-3/4" />
              <Bone className="h-3 w-20" />
            </div>
          </CardShell>
        ))}
      </div>
    </div>
  );
}

// ─── Simulator Skeleton ───
export function SimulatorSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="space-y-2">
        <Bone className="h-7 w-48" />
        <Bone className="h-4 w-64" />
      </div>

      <div className="flex gap-2">
        {[...Array(4)].map((_, i) => (
          <Bone key={i} className="h-10 w-28 rounded-xl" />
        ))}
      </div>

      <CardShell className="space-y-4">
        <Bone className="h-5 w-40" />
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Bone className="h-3 w-20" />
            <Bone className="h-10 w-full rounded-xl" />
          </div>
          <div className="space-y-2">
            <Bone className="h-3 w-20" />
            <Bone className="h-10 w-full rounded-xl" />
          </div>
        </div>
        <Bone className="h-40 w-full rounded-xl" />
      </CardShell>
    </div>
  );
}

// ─── Generic tab skeleton (fallback) ───
export function GenericSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="space-y-2">
        <Bone className="h-7 w-48" />
        <Bone className="h-4 w-64" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[...Array(4)].map((_, i) => (
          <CardShell key={i} className="h-40" />
        ))}
      </div>
    </div>
  );
}
